/**
 * Fleet UX: the category marks — the same shapes and colors as on the galaxy map (pixi/galaxy-map.ts):
 * survey = blue diamond, military = red shield, construction = amber hexagon, mixed = violet ring with
 * one dot per kind it contains, empty = grey ring. Reserve fleets get a neutral badge next to it.
 */
import { memo, useSyncExternalStore } from 'react';
import { CATEGORY_META, RESERVE_COLOR, type FleetCategory } from './fleet-category.ts';
import { SHIP_CLASS_ICONS, SHIP_CLASS_LABELS } from './fleet-format.ts';

const KIND_COLOR = { military: CATEGORY_META.military.color, survey: CATEGORY_META.survey.color, construction: CATEGORY_META.construction.color };

export const FleetCategoryIcon = memo(function FleetCategoryIcon({ category, kinds = [], size = 18 }: {
  category: FleetCategory; kinds?: Array<keyof typeof KIND_COLOR>; size?: number;
}) {
  const c = CATEGORY_META[category].color;
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" role="img" aria-label={CATEGORY_META[category].label} data-category-icon={category} className="flex-shrink-0">
      {category === 'survey' && <path d="M10 2 L18 10 L10 18 L2 10 Z" fill={`${c}33`} stroke={c} strokeWidth="1.8" />}
      {category === 'military' && <path d="M10 2 L17 5 V10 C17 14 14 17 10 18.5 C6 17 3 14 3 10 V5 Z" fill={`${c}33`} stroke={c} strokeWidth="1.8" />}
      {category === 'construction' && <path d="M10 2 L17 6 V14 L10 18 L3 14 V6 Z" fill={`${c}33`} stroke={c} strokeWidth="1.8" />}
      {category === 'mixed' && (
        <>
          <circle cx="10" cy="10" r="7.5" fill={`${c}22`} stroke={c} strokeWidth="1.8" />
          {kinds.map((k, i) => <circle key={k} cx={kinds.length === 1 ? 10 : 6 + (8 / (kinds.length - 1)) * i} cy="10" r="2.2" fill={KIND_COLOR[k]} />)}
        </>
      )}
      {category === 'empty' && <circle cx="10" cy="10" r="7" fill="none" stroke={c} strokeWidth="1.8" strokeDasharray="3 2" />}
    </svg>
  );
});

export function CategoryBadge({ category, kinds, reserve }: { category: FleetCategory; kinds: Array<keyof typeof KIND_COLOR>; reserve?: boolean }) {
  const m = CATEGORY_META[category];
  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap">
      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium" data-testid="fleet-category"
        style={{ color: m.color, background: `${m.color}18`, border: `1px solid ${m.color}40` }}>
        <FleetCategoryIcon category={category} kinds={kinds} size={14} />{m.label}
      </span>
      {reserve && (
        <span className="text-[11px] px-2 py-0.5 rounded uppercase tracking-wide" style={{ color: RESERVE_COLOR, border: `1px solid ${RESERVE_COLOR}50` }}>Tartalék</span>
      )}
    </span>
  );
}

/** Ship composition per class — a mixed fleet keeps every ship type visible. */
export function CompositionChips({ shipCounts }: { shipCounts: Record<string, number> }) {
  const entries = Object.entries(shipCounts).filter(([, n]) => n > 0);
  return (
    <span className="inline-flex flex-wrap gap-1" data-testid="fleet-composition">
      {entries.map(([cls, n]) => {
        const kind = (cls === 'scout' ? 'survey' : cls === 'construction' ? 'construction' : 'military') as keyof typeof KIND_COLOR;
        return (
          <span key={cls} className="inline-flex items-center gap-0.5 text-[11px] px-1.5 py-0.5 rounded bg-slate-900/70"
            style={{ color: KIND_COLOR[kind] }} title={SHIP_CLASS_LABELS[cls] ?? cls} data-ship-class={cls}>
            <span aria-hidden>{SHIP_CLASS_ICONS[cls] ?? '•'}</span>
            <span className="font-mono">{n}</span>
            <span className="sr-only">{SHIP_CLASS_LABELS[cls] ?? cls}</span>
          </span>
        );
      })}
    </span>
  );
}

// ---- one shared 1-second clock (instead of an interval per card) ----

let nowValue = Date.now();
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
function subscribe(cb: () => void) {
  listeners.add(cb);
  if (!timer) timer = setInterval(() => { nowValue = Date.now(); for (const l of listeners) l(); }, 1000);
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0 && timer) { clearInterval(timer); timer = null; }
  };
}
/** Re-renders the caller once per second — only components showing a countdown use it. */
export function useNow(): number {
  return useSyncExternalStore(subscribe, () => nowValue);
}

